import {
  ACOUSTIC_REFLEX_DATA_POINTS,
  ACOUSTIC_REFLEX_DECAY_DATA_POINTS,
  SPEECH_IN_NOISE_METHOD_TYPES
} from "constants/audiology";
import { HZ_VALUES } from "constants/audiogram";
import { type OtoacousticReport } from "features/patients/sections/patient-audiology/test-results/otoacoustic-emissions/otoacoustic.type";
import { type Report } from "services/types/reports.type";
import { isBlank } from "utils/common";

export type Ear = "L" | "R";

const asInteger = (value: string | number | null): number | null => {
  if (isBlank(value)) return null;
  return Number.isInteger(Number.parseInt(String(value))) ? Number(value) : null;
};

// Air/bone conduction thresholds are stored as one array of numeric values and a
// parallel array of annotations (e.g. "A", "CNT"). Resolve each index to a single
// numeric dB value — the primary array wins when it is itself an integer, else the
// secondary array — mirroring getGraphDataRow used by the audiogram graph and the
// diagnosis auto-calculation. Inlined to keep the merge-tag path free of the chart
// module's heavy transitive imports.
const resolveAudiogramRow = (
  primaryRow?: (string | null)[],
  secondaryRow?: (string | null)[]
): (number | null)[] =>
  (primaryRow ?? []).map((primaryValue, index) => {
    const primary = asInteger(primaryValue);
    if (primary !== null) return primary;
    return asInteger(secondaryRow?.[index] ?? null);
  });

// PTA is the mean air-conduction threshold at 500/1000/2000 Hz — indices 2/4/6 of
// the audiometric frequency array. Replicates the backend calculate_pta(): every
// one of the three values must be an integer, otherwise the result is null (a
// single missing/non-numeric value voids the average).
const PTA_INDICES = [2, 4, 6];

const parseBackendInt = (value: string | number | null): number | null => {
  if (isBlank(value)) return null;
  const asString = String(value).trim();
  if (!/^[+-]?\d+$/.test(asString)) return null;
  return Number(asString);
};

const reconstructRawRow = (
  numericRow?: (string | null)[],
  annotationRow?: (string | null)[]
): (string | null)[] => {
  const length = Math.max(numericRow?.length ?? 0, annotationRow?.length ?? 0);
  const result: (string | null)[] = [];

  for (let index = 0; index < length; index++) {
    const numeric = numericRow?.[index] ?? null;
    const annotation = annotationRow?.[index] ?? null;

    if (numeric === null) {
      result.push(annotation);
    } else if (annotation === null) {
      result.push(numeric);
    } else {
      result.push(`${annotation}_${numeric}`);
    }
  }

  return result;
};

const formatDbHl = (value?: number | string | null): string | null =>
  isBlank(value) ? null : `${value} dB HL`;

const getFrequencies = (
  report: Report,
  fallback: readonly number[]
): readonly number[] => {
  const frequencies = report.pureToneAudiogramReport?.frequencies;
  return frequencies && frequencies.length ? frequencies : fallback;
};

const formatThresholdBlock = (
  frequencies: readonly number[],
  values: (number | null)[]
): string | null => {
  const parts = frequencies
    .map((frequency, index) => {
      const value = values[index];
      if (isBlank(value)) return null;
      return `${frequency} Hz: ${value} dB`;
    })
    .filter((part): part is string => part !== null);

  return parts.length ? parts.join(", ") : null;
};

export const formatAirConductionThresholds = (
  report?: Report,
  ear: Ear = "L"
): string | null => {
  const pureTone = report?.pureToneAudiogramReport;
  if (!pureTone) return null;

  const values =
    ear === "L"
      ? resolveAudiogramRow(
          pureTone.airConductionL,
          pureTone.airConductionLAnnotation
        )
      : resolveAudiogramRow(
          pureTone.airConductionR,
          pureTone.airConductionRAnnotation
        );

  return formatThresholdBlock(getFrequencies(report, HZ_VALUES), values);
};

export const formatBoneConductionThresholds = (
  report?: Report,
  ear: Ear = "L"
): string | null => {
  const pureTone = report?.pureToneAudiogramReport;
  if (!pureTone) return null;

  const values =
    ear === "L"
      ? resolveAudiogramRow(
          pureTone.boneConductionL,
          pureTone.boneConductionLAnnotation
        )
      : resolveAudiogramRow(
          pureTone.boneConductionR,
          pureTone.boneConductionRAnnotation
        );

  return formatThresholdBlock(getFrequencies(report, HZ_VALUES), values);
};

export const formatPta = (report?: Report, ear: Ear = "L"): string | null => {
  const pureTone = report?.pureToneAudiogramReport;
  if (!pureTone) return null;

  const raw =
    ear === "L"
      ? reconstructRawRow(
          pureTone.airConductionL,
          pureTone.airConductionLAnnotation
        )
      : reconstructRawRow(
          pureTone.airConductionR,
          pureTone.airConductionRAnnotation
        );

  const values: number[] = [];
  for (const index of PTA_INDICES) {
    const value = parseBackendInt(raw[index] ?? null);
    if (value === null) return null;
    values.push(value);
  }

  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  return `${Math.round(mean * 100) / 100} dB`;
};

export const formatSrt = (report?: Report, ear: Ear = "L"): string | null => {
  const item =
    ear === "L"
      ? report?.speechAudiometryReport?.leftEar
      : report?.speechAudiometryReport?.rightEar;

  return formatDbHl(item?.value);
};

export const formatSrtMasking = (
  report?: Report,
  ear: Ear = "L"
): string | null => {
  const item =
    ear === "L"
      ? report?.speechAudiometryReport?.leftEar
      : report?.speechAudiometryReport?.rightEar;

  return formatDbHl(item?.masking);
};

export const formatWrsMasking = (
  report?: Report,
  ear: Ear = "L"
): string | null => {
  const item =
    ear === "L"
      ? report?.wordRecognitionReport?.leftEar
      : report?.wordRecognitionReport?.rightEar;

  return formatDbHl(item?.masking);
};

export const formatTympanometryAdmittance = (
  report?: Report,
  ear: Ear = "L"
): string | null => {
  const item =
    ear === "L"
      ? report?.tympanometryReport?.leftEar
      : report?.tympanometryReport?.rightEar;

  return isBlank(item?.admittance) ? null : `${item?.admittance} ml`;
};

export const formatTympanometryEcv = (
  report?: Report,
  ear: Ear = "L"
): string | null => {
  const item =
    ear === "L"
      ? report?.tympanometryReport?.leftEar
      : report?.tympanometryReport?.rightEar;

  return isBlank(item?.ecv) ? null : `${item?.ecv} cc`;
};

export const formatTympanometryPressure = (
  report?: Report,
  ear: Ear = "L"
): string | null => {
  const item =
    ear === "L"
      ? report?.tympanometryReport?.leftEar
      : report?.tympanometryReport?.rightEar;

  return isBlank(item?.pressure) ? null : `${item?.pressure} daPa`;
};

const ACOUSTIC_REFLEX_FREQUENCIES = [500, 1000, 2000, 4000] as const;
const ACOUSTIC_REFLEX_DECAY_FREQUENCIES = [500, 1000] as const;

const formatReflexValue = (
  value: string | number | null,
  isDecay: boolean
): string | null => {
  if (isBlank(value)) return null;
  if (typeof value === "number") return `${value} dB`;

  const source = isDecay
    ? ACOUSTIC_REFLEX_DECAY_DATA_POINTS
    : ACOUSTIC_REFLEX_DATA_POINTS;

  return source.getFromCode(value) || String(value);
};

const formatReflexRow = (
  frequencies: readonly number[],
  values: (string | number | null)[] | undefined,
  isDecay: boolean
): string | null => {
  if (!values) return null;

  const parts = frequencies
    .map((frequency, index) => {
      const formatted = formatReflexValue(values[index] ?? null, isDecay);
      return formatted === null ? null : `${frequency} Hz: ${formatted}`;
    })
    .filter((part): part is string => part !== null);

  return parts.length ? parts.join(", ") : null;
};

export const formatAcousticReflex = (
  report?: Report,
  ear: Ear = "L"
): string | null => {
  const reflex = report?.acousticReflexReport;
  if (!reflex) return null;

  const ipsilateral = formatReflexRow(
    ACOUSTIC_REFLEX_FREQUENCIES,
    ear === "L" ? reflex.ipsilateralL : reflex.ipsilateralR,
    false
  );
  const contralateral = formatReflexRow(
    ACOUSTIC_REFLEX_FREQUENCIES,
    ear === "L" ? reflex.contralateralL : reflex.contralateralR,
    false
  );

  const sections: string[] = [];
  if (ipsilateral) sections.push(`Ipsilateral — ${ipsilateral}`);
  if (contralateral) sections.push(`Contralateral — ${contralateral}`);

  return sections.length ? sections.join("; ") : null;
};

export const formatAcousticReflexDecay = (
  report?: Report,
  ear: Ear = "L"
): string | null => {
  const decay = report?.acousticReflexDecayReport;
  if (!decay) return null;

  const ipsilateral = formatReflexRow(
    ACOUSTIC_REFLEX_DECAY_FREQUENCIES,
    ear === "L" ? decay.ipsilateralL : decay.ipsilateralR,
    true
  );
  const contralateral = formatReflexRow(
    ACOUSTIC_REFLEX_DECAY_FREQUENCIES,
    ear === "L" ? decay.contralateralL : decay.contralateralR,
    true
  );

  const sections: string[] = [];
  if (ipsilateral) sections.push(`Ipsilateral — ${ipsilateral}`);
  if (contralateral) sections.push(`Contralateral — ${contralateral}`);

  return sections.length ? sections.join("; ") : null;
};

export const formatQuickSinMethod = (
  report?: Report,
  ear: Ear = "L"
): string | null => {
  const method =
    ear === "L"
      ? report?.speechInNoiseReport?.leftEar?.method
      : report?.speechInNoiseReport?.rightEar?.method;

  if (isBlank(method)) return null;

  return SPEECH_IN_NOISE_METHOD_TYPES.getFromCode(method) || String(method);
};

type OaeEmission = {
  label: string;
  code: "DPOAE" | "TEOAE" | "SOAE";
  isSelected: (oae: OtoacousticReport, ear: Ear) => boolean;
  getValues: (oae: OtoacousticReport, ear: Ear) => (string | null)[];
};

// Precedence: distortion product > transient > spontaneous. An ear can technically
// have more than one emission selected; OAE Type surfaces the highest-precedence
// one, OAE Results lists every selected emission.
const OAE_EMISSIONS: OaeEmission[] = [
  {
    label: "Distortion Product",
    code: "DPOAE",
    isSelected: (oae, ear) =>
      ear === "L"
        ? oae.is_distortion_product_l_selected
        : oae.is_distortion_product_r_selected,
    getValues: (oae, ear) =>
      ear === "L" ? oae.distortion_product_l : oae.distortion_product_r
  },
  {
    label: "Transient",
    code: "TEOAE",
    isSelected: (oae, ear) =>
      ear === "L" ? oae.is_transient_l_selected : oae.is_transient_r_selected,
    getValues: (oae, ear) =>
      ear === "L" ? oae.transient_l : oae.transient_r
  },
  {
    label: "Spontaneous",
    code: "SOAE",
    isSelected: (oae, ear) =>
      ear === "L"
        ? oae.is_spontaneous_l_selected
        : oae.is_spontaneous_r_selected,
    getValues: (oae, ear) =>
      ear === "L" ? oae.spontaneous_l : oae.spontaneous_r
  }
];

export const formatOaeType = (
  report?: Report,
  ear: Ear = "L"
): string | null => {
  const oae = report?.otoacoustic_emissions_report;
  if (!oae) return null;

  const emission = OAE_EMISSIONS.find(({ isSelected }) => isSelected(oae, ear));
  return emission ? emission.code : null;
};

export const formatOaeResults = (
  report?: Report,
  ear: Ear = "L"
): string | null => {
  const oae = report?.otoacoustic_emissions_report;
  if (!oae) return null;

  const frequencies = oae.frequencies ?? oae.frequencies_data ?? [];

  const sections = OAE_EMISSIONS.filter(({ isSelected }) =>
    isSelected(oae, ear)
  )
    .map(({ label, getValues }) => {
      const values = getValues(oae, ear);
      const parts = frequencies
        .map((frequency, index) => {
          const value = values?.[index];
          return isBlank(value) ? null : `${frequency} Hz: ${value}`;
        })
        .filter((part): part is string => part !== null);

      return parts.length ? `${label}: ${parts.join(", ")}` : null;
    })
    .filter((section): section is string => section !== null);

  return sections.length ? sections.join("; ") : null;
};

export const formatIcd10Codes = (report?: Report): string | null => {
  const codes = report?.diagnosis?.icd10_codes;
  if (!codes || !codes.length) return null;

  const parts = codes
    .map((code) => code?.code)
    .filter((code): code is string => !isBlank(code));

  return parts.length ? parts.join(", ") : null;
};

export const formatCustomTests = (report?: Report): string | null => {
  const tests = report?.customTestReports;
  if (!tests || !tests.length) return null;

  const parts = tests
    .filter((test) => test.is_selected)
    .map((test) => {
      const name = isBlank(test.name) ? null : test.name;
      const details = isBlank(test.details) ? null : test.details;
      if (!name && !details) return null;
      if (!details) return name;
      if (!name) return details;
      return `${name}: ${details}`;
    })
    .filter((part): part is string => part !== null);

  return parts.length ? parts.join("\n") : null;
};

export const formatFullAudiogram = (report?: Report): string | null => {
  const pureTone = report?.pureToneAudiogramReport;
  if (!pureTone) return null;

  const frequencies = getFrequencies(report, HZ_VALUES);
  const airLeft = resolveAudiogramRow(
    pureTone.airConductionL,
    pureTone.airConductionLAnnotation
  );
  const airRight = resolveAudiogramRow(
    pureTone.airConductionR,
    pureTone.airConductionRAnnotation
  );
  const boneLeft = resolveAudiogramRow(
    pureTone.boneConductionL,
    pureTone.boneConductionLAnnotation
  );
  const boneRight = resolveAudiogramRow(
    pureTone.boneConductionR,
    pureTone.boneConductionRAnnotation
  );

  const cell = (value: number | null) =>
    isBlank(value) ? "-" : `${value} dB`;

  const rows = frequencies
    .map((frequency, index) => {
      const values = [
        airLeft[index],
        airRight[index],
        boneLeft[index],
        boneRight[index]
      ];
      if (values.every((value) => isBlank(value))) return null;
      return `${frequency} Hz | AC L: ${cell(airLeft[index])}, AC R: ${cell(
        airRight[index]
      )}, BC L: ${cell(boneLeft[index])}, BC R: ${cell(boneRight[index])}`;
    })
    .filter((row): row is string => row !== null);

  return rows.length ? rows.join("\n") : null;
};
